import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, errorLines, errorMessage, getProjectStatistics, listCampaignReport, listProjectsByStatus } from '../../api';
import { paths } from '../../app/paths';
import { useAsyncData } from '../../hooks/useAsyncData';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { formatDate } from '../../lib/format';
import { saveBlob } from '../../lib/saveBlob';
import type { CampaignReportData, Project, ReportCampaignRow } from '../../types';
import { Button, Card, EmptyState, ErrorState, InlineAlert, Pagination, ProgressBar, Skeleton, StatusBadge, Table, Tabs, Td, TextField, Th } from '../../ui';
import { num, pct } from './metrics';
import { browserTimeZone, datesOutOfOrder, rowFromStatistics, summaryToCsv, today } from './reportsModel';

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'closed', label: 'Closed' },
  { key: 'draft', label: 'Drafts' },
];
const PAGE_SIZE = 20;
const FALLBACK_PAGE_SIZE = 10;

interface Params { status: string; search: string; from: string; to: string; tz: string; page: number }

/**
 * TEMPORARY FALLBACK: used only while the backend has no GET /reports/campaigns (a 404). It lists one status page by page and loads each
 * campaign's own statistics (one call per row, the same figures). It cannot search, filter by date or give totals for the whole set; remove it
 * (and the notice in the page) once the one-call report is on the build everyone uses.
 */
async function loadFallback({ status, page }: Params, signal: AbortSignal): Promise<CampaignReportData> {
  const list: { items: Project[]; total: number; totalPages: number } = await listProjectsByStatus(status, page, FALLBACK_PAGE_SIZE, { signal });
  const settled = await Promise.allSettled(list.items.map((p) => getProjectStatistics(p.id, { signal })));
  const items = list.items.map((project, i) => {
    const r = settled[i];
    if (r.status === 'fulfilled' && r.value) return rowFromStatistics(project, r.value);
    return rowFromStatistics(project, null, r.status === 'rejected' ? errorMessage(r.reason) : 'No statistics for this campaign.');
  });
  return { items, total: list.total, page, size: FALLBACK_PAGE_SIZE, totalPages: list.totalPages, totals: null, filters: null, fallback: true };
}

async function loadReport(params: Params, signal: AbortSignal): Promise<CampaignReportData> {
  try {
    const data: CampaignReportData = await listCampaignReport({ status: params.status, search: params.search, from: params.from, to: params.to, tz: params.tz, page: params.page, size: PAGE_SIZE }, { signal });
    return { ...data, fallback: false };
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return loadFallback(params, signal); // the route is not on this backend yet
    throw err;
  }
}

/**
 * Reports: how every campaign is doing, in ONE call to GET /reports/campaigns (newest first, paged by the server). Filters: status, a name
 * search, and the days respondents entered (from / to, both inclusive, cut in the browser's time zone). The totals row covers the whole
 * filtered set: counts added up and rates recalculated from them by the server, never averaged here. Figures are the server's own and shown as
 * sent: one it could not work out is a dash, never a zero. "Redirects" means the figures come only from the survey links; "Respondents" means
 * they come from respondent sessions (the entry link). Drop-out and conversion exist only for the latter. The CSV is exactly the rows shown.
 */
export function ReportsPage() {
  const [tab, setTab] = useState('active');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const tz = browserTimeZone();
  const query = useDebouncedValue(search.trim(), 300);
  const outOfOrder = datesOutOfOrder(from, to);

  const report = useAsyncData<CampaignReportData>(
    (signal) => loadReport({ status: tab, search: query, from, to, tz, page }, signal),
    [tab, query, from, to, tz, page],
    { enabled: !outOfOrder }
  );
  const data = report.data;
  const fallback = Boolean(data && data.fallback);
  const rows: ReportCampaignRow[] = data ? data.items : [];
  const failed = rows.filter((r) => r.error).length;
  const totals = data ? data.totals : null;
  const hasFilters = Boolean(query || from || to);

  const reset = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => { setter(e.target.value); setPage(1); };
  const changeTab = (key: string) => { setTab(key); setPage(1); };
  const clearFilters = () => { setSearch(''); setFrom(''); setTo(''); setPage(1); };
  const download = () => {
    if (rows.length === 0) return;
    saveBlob(new Blob(['﻿', summaryToCsv(rows)], { type: 'text/csv;charset=utf-8' }), `campaign-summary-${today()}.csv`);
  };

  // the fallback adds up the rows shown only, and says so
  const pageWanted = rows.reduce((sum, r) => sum + (r.wantedCompletes || 0), 0);
  const pageComplete = rows.reduce((sum, r) => sum + r.complete, 0);

  let body: React.ReactNode;
  if (outOfOrder) {
    body = <EmptyState title="Check the dates" description="The first day must not be after the last day." />;
  } else if (report.error) {
    const lines: string[] = errorLines(report.error);
    body = lines.length > 1
      ? <div className="p-5"><InlineAlert><b>{lines[0]}</b><ul className="ml-4 mt-1 list-disc">{lines.slice(1).map((m) => <li key={m}>{m}</li>)}</ul></InlineAlert><div className="mt-3"><Button variant="secondary" size="sm" onClick={report.reload}>Try again</Button></div></div>
      : <ErrorState title="Could not load the report" message={errorMessage(report.error)} onRetry={report.reload} />;
  } else if (!data) {
    body = <div className="space-y-3 p-4" aria-busy="true" aria-label="Loading campaigns">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</div>;
  } else if (rows.length === 0) {
    body = <EmptyState title={hasFilters ? 'No campaigns match these filters' : `No ${tab} campaigns`} description={hasFilters ? 'Try another name or period.' : 'Campaigns appear here once they exist in this status.'} />;
  } else {
    body = (
      <>
        <Table caption="Campaign report summary">
          <thead>
            <tr>
              <Th>Campaign</Th><Th>Status</Th><Th numeric>Wanted</Th><Th numeric>Completes</Th><Th>Progress</Th>
              <Th numeric title="Timed out ÷ (complete + screen-out + timed out), as the server calculates it. Respondent sessions only.">Drop-out</Th>
              <Th numeric title="Completes ÷ entries (every entry-link arrival except possible duplicates), as the server calculates it. Respondent sessions only.">Conversion</Th>
              <Th numeric title="Median survey time of the completes. Respondent sessions only.">Median length</Th>
              <Th>Last activity</Th><Th>Based on</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-canvas">
                <Td className="font-semibold">
                  <Link to={paths.report(r.id)} className="text-brand-700 hover:underline">{r.name || r.id}</Link>
                  {r.error && <span className="mt-0.5 block text-xs font-normal text-danger">{r.error}</span>}
                </Td>
                <Td><StatusBadge status={r.projectStatus} /></Td>
                <Td numeric className="tabular-nums text-muted">{r.error ? '—' : num(r.wantedCompletes)}</Td>
                <Td numeric className="tabular-nums">{r.error ? '—' : num(r.complete)}</Td>
                <Td>{!r.error && typeof r.progress === 'number' ? <ProgressBar value={Math.round(r.progress)} label={`${r.name || 'Campaign'} progress`} /> : <span className="text-muted">—</span>}</Td>
                <Td numeric className="tabular-nums text-muted">{pct(r.dropOutRate)}</Td>
                <Td numeric className="tabular-nums text-muted">{pct(r.conversionRate)}</Td>
                <Td numeric className="tabular-nums text-muted">{typeof r.medianLoiMinutes === 'number' ? `${r.medianLoiMinutes} min` : '—'}</Td>
                <Td className="whitespace-nowrap text-xs text-muted">{r.lastActivityAt ? formatDate(r.lastActivityAt) : '—'}</Td>
                <Td className="text-xs text-muted">{r.error || !r.source ? '—' : r.source === 'sessions' ? 'Respondents' : 'Redirects'}</Td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {totals ? (
              <tr className="border-t border-line bg-canvas">
                <Td className="font-bold">All {totals.campaigns} {totals.campaigns === 1 ? 'campaign' : 'campaigns'}<span className="block text-xs font-normal text-muted">whole filtered set, not just this page</span></Td><Td />
                <Td numeric className="font-bold tabular-nums">{num(totals.wantedCompletes)}</Td>
                <Td numeric className="font-bold tabular-nums">{num(totals.complete)}</Td>
                <Td className="font-bold tabular-nums">{pct(totals.progress)}</Td>
                <Td numeric className="font-bold tabular-nums">{pct(totals.dropOutRate)}</Td>
                <Td numeric className="font-bold tabular-nums">{pct(totals.conversionRate)}</Td>
                <Td numeric className="font-bold tabular-nums">{typeof totals.medianLoiMinutes === 'number' ? `${totals.medianLoiMinutes} min` : '—'}</Td>
                <Td colSpan={2} className="text-xs text-muted">Respondent figures cover {totals.campaignsWithSessions} of {totals.campaigns}</Td>
              </tr>
            ) : (
              <tr className="border-t border-line bg-canvas">
                <Td className="font-bold">This page only</Td><Td />
                <Td numeric className="font-bold tabular-nums">{num(pageWanted)}</Td>
                <Td numeric className="font-bold tabular-nums">{num(pageComplete)}</Td>
                <Td colSpan={6} />
              </tr>
            )}
          </tfoot>
        </Table>
        <Pagination page={page} pageSize={data.size} total={data.total} onPageChange={setPage} disabled={report.loading} />
      </>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-ink">Reports</h1>
          <p className="mt-1 text-sm text-muted">How each campaign is doing. Open one for its full report.</p>
        </div>
        <Button variant="secondary" onClick={download} disabled={rows.length === 0}>Download summary CSV</Button>
      </div>

      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <Tabs tabs={TABS} value={tab} onChange={changeTab} label="Campaign status" />
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full sm:w-56"><TextField label="Search by name" type="search" value={search} onChange={reset(setSearch)} disabled={fallback} /></div>
          <TextField label="Entered from" type="date" value={from} max={to || undefined} onChange={reset(setFrom)} disabled={fallback} />
          <TextField label="Entered to" type="date" value={to} min={from || undefined} onChange={reset(setTo)} disabled={fallback} error={outOfOrder ? 'Not before the first day' : undefined} />
          {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>}
        </div>
      </div>

      {fallback && <div className="mb-3"><InlineAlert tone="info">Showing basic figures: this server does not have the one-call report yet, so search, dates and whole-set totals are off and each campaign is loaded on its own.</InlineAlert></div>}
      {failed > 0 && <div className="mb-3"><InlineAlert tone="warn">{failed} {failed === 1 ? 'campaign has' : 'campaigns have'} no figures. The summary shows a dash for {failed === 1 ? 'it' : 'them'}.</InlineAlert></div>}
      {(from || to) && !fallback && <p className="mb-3 text-xs text-muted">Counting respondents who entered {from ? `from ${from}` : ''}{from && to ? ' ' : ''}{to ? `to ${to}` : ''} ({tz}).</p>}

      <Card className="overflow-hidden">{body}</Card>
      <p className="mt-3 text-xs text-muted">Figures are the server's own. "Redirects" means they come only from the survey links; "Respondents" means they come from respondent sessions. Drop-out and conversion exist only for the latter.</p>
    </div>
  );
}
